import type { ImpactEffortPoint } from "@playbookai/widget-schemas";
import {
  impactEffortPillCenterXPx,
  impactEffortPillCenterYPx,
  impactEffortPillSize,
} from "./fan-out.js";

export interface ImpactEffortDeclutterOffset {
  readonly dx: number;
  readonly dy: number;
}

export interface ImpactEffortDeclutterOptions {
  readonly plot: { readonly width: number; readonly height: number };
  readonly positionFor: (point: ImpactEffortPoint) => {
    readonly xPct: number;
    readonly yPct: number;
  };
  readonly labelFor?: ((point: ImpactEffortPoint) => string) | undefined;
  readonly gapPx?: number | undefined;
  readonly insetPx?: number | undefined;
}

interface MarkerBox {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

interface Candidate {
  readonly x: number;
  readonly y: number;
  readonly compactDistance: number;
  readonly distance: number;
}

const FALLBACK_GAPS = [6, 2, 0] as const;

/**
 * Move only the rendered marker boxes, never their score coordinates. Points
 * are placed in their existing order so adding a later idea cannot reshuffle
 * the markers that already fit. Candidate selection is deterministic and
 * prefers the point's true quadrant before considering the other half of the
 * plot.
 */
export function declutterImpactEffortMarkers(
  points: readonly ImpactEffortPoint[],
  {
    plot,
    positionFor,
    labelFor = (point) => point.label,
    gapPx = FALLBACK_GAPS[0],
    insetPx = 4,
  }: ImpactEffortDeclutterOptions,
): ReadonlyMap<string, ImpactEffortDeclutterOffset> {
  const width = Math.max(1, plot.width);
  const height = Math.max(1, plot.height);
  const sizes = new Map(
    points.map((point) => [point.id, impactEffortPillSize(labelFor(point))]),
  );
  const slotWidth =
    Math.max(1, ...[...sizes.values()].map((size) => size.width)) + gapPx;
  const slotHeight =
    Math.max(1, ...[...sizes.values()].map((size) => size.height)) + gapPx;
  const occupied: MarkerBox[] = [];
  const offsets = new Map<string, ImpactEffortDeclutterOffset>();

  for (const point of points) {
    const size = sizes.get(point.id);
    if (!size) continue;
    const position = positionFor(point);
    const anchor = {
      x: impactEffortPillCenterXPx(position.xPct, width, size.width),
      y: impactEffortPillCenterYPx(position.yPct, height, size.height),
    };
    let selected: Candidate | undefined;
    const candidatesByGap = new Map<number, readonly Candidate[]>();
    const candidatesAtGap = (candidateGap: number): readonly Candidate[] => {
      const cached = candidatesByGap.get(candidateGap);
      if (cached) return cached;
      const candidates = candidatesFor(
        anchor,
        size,
        occupied,
        { width, height },
        slotWidth,
        slotHeight,
        candidateGap,
      );
      candidatesByGap.set(candidateGap, candidates);
      return candidates;
    };

    for (const keepQuadrant of [true, false]) {
      for (const candidateGap of uniqueGaps(gapPx)) {
        selected = candidatesAtGap(candidateGap).find(
          (candidate) =>
            insidePlot(candidate, size, width, height, insetPx) &&
            (!keepQuadrant ||
              inAnchorQuadrant(candidate, position, width, height)) &&
            !overlapsAny(candidate, size, occupied, candidateGap),
        );
        if (selected) break;
      }
      if (selected) break;
    }

    if (!selected) {
      selected = candidatesAtGap(0)
        .filter((candidate) =>
          insidePlot(candidate, size, width, height, insetPx),
        )
        .sort((left, right) => {
          const overlapDelta =
            overlapArea(left, size, occupied) -
            overlapArea(right, size, occupied);
          return overlapDelta || compareCandidates(left, right);
        })[0];
    }

    const visible = selected ?? {
      ...anchor,
      compactDistance: 0,
      distance: 0,
    };
    occupied.push({ ...visible, ...size });
    offsets.set(point.id, {
      dx: visible.x - anchor.x,
      dy: visible.y - anchor.y,
    });
  }

  return offsets;
}

function uniqueGaps(preferred: number): readonly number[] {
  return [...new Set([Math.max(0, preferred), ...FALLBACK_GAPS])];
}

function candidatesFor(
  anchor: { readonly x: number; readonly y: number },
  size: { readonly width: number; readonly height: number },
  occupied: readonly MarkerBox[],
  plot: { readonly width: number; readonly height: number },
  slotWidth: number,
  slotHeight: number,
  gap: number,
): Candidate[] {
  const candidates: Candidate[] = [];
  const seen = new Set<string>();
  const add = (x: number, y: number): void => {
    const key = `${x.toFixed(3)}:${y.toFixed(3)}`;
    if (seen.has(key)) return;
    seen.add(key);
    const dx = x - anchor.x;
    const dy = y - anchor.y;
    candidates.push({
      x,
      y,
      compactDistance: Math.hypot(dx / slotWidth, dy / slotHeight),
      distance: Math.hypot(dx, dy),
    });
  };

  add(anchor.x, anchor.y);
  for (const box of occupied) {
    const xSeparation = (size.width + box.width) / 2 + gap;
    const ySeparation = (size.height + box.height) / 2 + gap;
    add(box.x - xSeparation, anchor.y);
    add(box.x + xSeparation, anchor.y);
    add(anchor.x, box.y - ySeparation);
    add(anchor.x, box.y + ySeparation);
    add(box.x - xSeparation, box.y - ySeparation);
    add(box.x + xSeparation, box.y - ySeparation);
    add(box.x - xSeparation, box.y + ySeparation);
    add(box.x + xSeparation, box.y + ySeparation);
  }

  const maxRing =
    Math.max(
      Math.ceil(plot.width / slotWidth),
      Math.ceil(plot.height / slotHeight),
    ) + 2;
  for (let ring = 1; ring <= maxRing; ring += 1) {
    for (let row = -ring; row <= ring; row += 1) {
      for (let column = -ring; column <= ring; column += 1) {
        if (Math.max(Math.abs(row), Math.abs(column)) !== ring) continue;
        add(anchor.x + column * slotWidth, anchor.y + row * slotHeight);
      }
    }
  }

  return candidates.sort(compareCandidates);
}

function compareCandidates(left: Candidate, right: Candidate): number {
  return (
    left.compactDistance - right.compactDistance ||
    left.distance - right.distance ||
    left.y - right.y ||
    left.x - right.x
  );
}

function insidePlot(
  candidate: { readonly x: number; readonly y: number },
  size: { readonly width: number; readonly height: number },
  width: number,
  height: number,
  inset: number,
): boolean {
  return (
    candidate.x - size.width / 2 >= inset &&
    candidate.x + size.width / 2 <= width - inset &&
    candidate.y - size.height / 2 >= inset &&
    candidate.y + size.height / 2 <= height - inset
  );
}

function inAnchorQuadrant(
  candidate: { readonly x: number; readonly y: number },
  anchor: { readonly xPct: number; readonly yPct: number },
  width: number,
  height: number,
): boolean {
  const sameXHalf =
    anchor.xPct === 50 ||
    (anchor.xPct < 50 ? candidate.x <= width / 2 : candidate.x >= width / 2);
  const sameYHalf =
    anchor.yPct === 50 ||
    (anchor.yPct < 50 ? candidate.y <= height / 2 : candidate.y >= height / 2);
  return sameXHalf && sameYHalf;
}

function overlapsAny(
  candidate: { readonly x: number; readonly y: number },
  size: { readonly width: number; readonly height: number },
  occupied: readonly MarkerBox[],
  gap: number,
): boolean {
  return occupied.some(
    (box) =>
      Math.abs(candidate.x - box.x) < (size.width + box.width) / 2 + gap &&
      Math.abs(candidate.y - box.y) < (size.height + box.height) / 2 + gap,
  );
}

function overlapArea(
  candidate: { readonly x: number; readonly y: number },
  size: { readonly width: number; readonly height: number },
  occupied: readonly MarkerBox[],
): number {
  return occupied.reduce((area, box) => {
    const overlapWidth = Math.max(
      0,
      (size.width + box.width) / 2 - Math.abs(candidate.x - box.x),
    );
    const overlapHeight = Math.max(
      0,
      (size.height + box.height) / 2 - Math.abs(candidate.y - box.y),
    );
    return area + overlapWidth * overlapHeight;
  }, 0);
}
